import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';

const TherapieContainer = styled.div`
  min-height: 100vh;
  padding-top: 100px;
`;

const HeroSection = styled.section`
  position: relative;
  height: 20vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  overflow: hidden;
`;

const HeroContent = styled.div`
  text-align: center;
  max-width: 800px;
  padding: 0 2rem;
  z-index: 2;
`;

const HeroTitle = styled(motion.h1)`
  font-family: ${props => props.theme.fonts.script};
  font-size: clamp(3rem, 6vw, 5rem);
  font-weight: 700;
  margin: 0;
  text-shadow: 3px 3px 6px rgba(0, 0, 0, 0.4);
`;

const ContentSection = styled.section`
  padding: 4rem 0;
  background: ${props => props.theme.colors.backgroundLight};
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 3rem;
  margin-top: 3rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const InfoContent = styled.div`
  h3 {
    font-size: 2rem;
    color: ${props => props.theme.colors.text};
    margin-bottom: 1rem;
  }
  
  p {
    font-size: 1.1rem;
    line-height: 1.8;
    color: ${props => props.theme.colors.textLight};
    margin-bottom: 1.5rem;
  }
  
  .highlight-box {
    background: white;
    padding: 2rem;
    border-radius: 15px;
    margin: 2rem 0;
    box-shadow: ${props => props.theme.shadows.sm};
    
    h4 {
      color: ${props => props.theme.colors.primary};
      margin-bottom: 1rem;
    }
    
    ul {
      list-style: none;
      padding: 0;
      
      li {
        padding: 0.5rem 0;
        
        &::before {
          content: '🌟';
          margin-right: 0.5rem;
        }
      }
    }
  }
`;

const InfoSidebar = styled.div`
  .info-card {
    background: white;
    padding: 2rem;
    border-radius: 20px;
    box-shadow: ${props => props.theme.shadows.card};
    margin-bottom: 2rem;
    
    h4 {
      color: ${props => props.theme.colors.primary};
      margin-bottom: 1rem;
      font-size: 1.3rem;
    }
    
    .info-item {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 0.75rem 0;
      border-bottom: 1px solid ${props => props.theme.colors.lightGray};
      
      &:last-child {
        border-bottom: none;
      }
      
      .label {
        color: ${props => props.theme.colors.textLight};
      }
      
      .value {
        font-weight: 600;
        color: ${props => props.theme.colors.text};
      }
    }
  }
`;

const TargetGroupsSection = styled.section`
  padding: 4rem 0;
  background: white;
`;

const TargetGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-top: 3rem;
`;

const TargetCard = styled(motion.div)`
  background: ${props => props.theme.colors.backgroundLight};
  padding: 2rem;
  border-radius: 20px;
  text-align: center;
  
  .icon {
    font-size: 3rem;
    margin-bottom: 1rem;
  }
  
  h4 {
    font-size: 1.3rem;
    color: ${props => props.theme.colors.text};
    margin-bottom: 1rem;
  }
  
  p {
    color: ${props => props.theme.colors.textLight};
    line-height: 1.6;
    margin-bottom: 1rem;
  }
  
  .conditions {
    list-style: none;
    padding: 0;
    font-size: 0.9rem;
    
    li {
      padding: 0.25rem 0;
      color: ${props => props.theme.colors.textLight};
      
      &::before {
        content: '•';
        color: ${props => props.theme.colors.primary};
        margin-right: 0.5rem;
      }
    }
  }
`;

const ApproachSection = styled.section`
  padding: 4rem 0;
  background: ${props => props.theme.colors.gradients.card};
`;

const ApproachGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
  margin-top: 3rem;
`;

const ApproachCard = styled(motion.div)`
  background: white;
  padding: 2rem;
  border-radius: 20px;
  text-align: center;
  box-shadow: ${props => props.theme.shadows.card};
  
  .step {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 50px;
    height: 50px;
    background: linear-gradient(45deg, ${props => props.theme.colors.primary}, ${props => props.theme.colors.secondary});
    color: white;
    border-radius: 50%;
    font-size: 1.5rem;
    font-weight: 700;
    margin-bottom: 1rem;
  }
  
  h4 {
    color: ${props => props.theme.colors.text};
    margin-bottom: 1rem;
  }
  
  p {
    color: ${props => props.theme.colors.textLight};
    line-height: 1.6;
  }
`;

const ContactSection = styled.section`
  padding: 4rem 0;
  background: white;
`;

const ContactCard = styled.div`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 3rem 2rem;
  border-radius: 20px;
  text-align: center;
  
  h3 {
    font-size: 2rem;
    margin-bottom: 1rem;
  }
  
  p {
    font-size: 1.1rem;
    margin-bottom: 2rem;
    line-height: 1.6;
    opacity: 0.9;
  }
  
  .contact-details {
    display: flex;
    justify-content: center;
    gap: 2rem;
    flex-wrap: wrap;
    
    .contact-item {
      text-align: center;
      
      .icon {
        font-size: 2rem;
        margin-bottom: 0.5rem;
      }
      
      .value {
        font-weight: 600;
        
        a {
          color: white;
          text-decoration: none;
          
          &:hover {
            text-decoration: underline;
          }
        }
      }
    }
  }
`;

const HeilpaedagogischesReiten = () => {
  const targetGroups = [
    {
      icon: '🧒',
      title: 'Kinder & Jugendliche',
      description: 'Förderung bei Entwicklungsverzögerungen, Verhaltensproblemen oder besonderen Bedürfnissen.',
      conditions: ['ADHS/ADS', 'Autismus-Spektrum', 'Entwicklungsverzögerungen', 'Emotionale Probleme', 'Soziale Ängste']
    },
    {
      icon: '🧠',
      title: 'Neurologische Erkrankungen',
      description: 'Unterstützung bei neurologischen Beeinträchtigungen durch die Bewegung des Pferdes.',
      conditions: ['Cerebralparese', 'Spina bifida', 'Multiple Sklerose', 'Schlaganfall-Folgen', 'Koordinationsstörungen']
    },
    {
      icon: '💪',
      title: 'Körperliche Beeinträchtigungen',
      description: 'Verbesserung von Motorik, Balance und Körperwahrnehmung.',
      conditions: ['Muskelschwäche', 'Gleichgewichtsstörungen', 'Haltungsschäden', 'Bewegungseinschränkungen']
    },
    {
      icon: '❤️',
      title: 'Psychische Belastungen',
      description: 'Emotionale Stabilisierung und Stärkung des Selbstvertrauens.',
      conditions: ['Depressionen', 'Angststörungen', 'Traumata', 'Selbstwertprobleme', 'Burn-out']
    }
  ];

  const approach = [
    {
      step: '1',
      title: 'Erstgespräch',
      description: 'Ausführliches Gespräch über Ziele, Bedürfnisse und medizinische Voraussetzungen.'
    },
    {
      step: '2',
      title: 'Pony-Auswahl',
      description: 'Sorgfältige Auswahl des passenden Ponys basierend auf individuellen Bedürfnissen.'
    },
    {
      step: '3',
      title: 'Therapieplan',
      description: 'Entwicklung eines individuellen Therapieplans mit konkreten Zielen.'
    },
    {
      step: '4',
      title: 'Durchführung',
      description: 'Regelmäßige Therapiesitzungen mit kontinuierlicher Anpassung des Programms.'
    },
    {
      step: '5',
      title: 'Evaluation',
      description: 'Regelmäßige Bewertung des Fortschritts und Anpassung der Therapieziele.'
    }
  ];

  return (
    <TherapieContainer>
      <Helmet>
        <title>Heilpädagogisches Reiten - Der Ponystall | Tiergestützte Therapie</title>
        <meta name="description" content="Professionelles heilpädagogisches Reiten für Menschen mit besonderen Bedürfnissen. Individuelle Therapie mit speziell ausgebildeten Ponys." />
      </Helmet>

      <HeroSection>
        <HeroContent>
          <HeroTitle
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.3 }}
          >
            Heilpädagogisches Reiten
          </HeroTitle>
        </HeroContent>
      </HeroSection>

      <ContentSection>
        <Container>
          <InfoGrid>
            <InfoContent>
              <h3>Heilkraft der Pferde</h3>
              <p>
                Das heilpädagogische Reiten ist eine Möglichkeit, die heilsame 
                Wirkung des Pferdes therapeutisch zu nutzen. Die dreidimensionale Bewegung des 
                Pferdes überträgt sich auf den Reiter und fördert sowohl körperliche als 
                auch emotionale Entwicklung.
              </p>
              <p>
                Wir arbeiten mit besonders ruhigen und 
                sensiblen Ponys. Jede Therapieeinheit wird individuell auf die 
                Bedürfnisse und Fähigkeiten des Klienten abgestimmt.
              </p>

              <div className="highlight-box">
                <h4>Therapeutische Wirkungen:</h4>
                <ul>
                  <li>Verbesserung der Motorik und Koordination</li>
                  <li>Stärkung der Rumpfmuskulatur</li>
                  <li>Förderung des Gleichgewichts</li>
                  <li>Steigerung des Selbstvertrauens</li>
                  <li>Emotionale Stabilisierung</li>
                  <li>Verbesserung der Konzentration</li>
                  <li>Förderung sozialer Kompetenzen</li>
                  <li>Stressabbau und Entspannung</li>
                </ul>
              </div>
            </InfoContent>

            <InfoSidebar>
              <div className="info-card">
                <h4>Rahmenbedingungen</h4>
                <div className="info-item">
                  <span className="label">Alter:</span>
                  <span className="value">Alle Altersgruppen</span>
                </div>

                <div className="info-item">
                  <span className="label">Häufigkeit:</span>
                  <span className="value">1x wöchentlich</span>
                </div>

              </div>


            </InfoSidebar>
          </InfoGrid>
        </Container>
      </ContentSection>

      <ContactSection>
        <Container>
          <ContactCard>
            <h3>Interesse an heilpädagogischem Reiten?</h3>
            <p>
              Gerne beraten wir Sie in einem persönlichen Gespräch über die Möglichkeiten 
              der tiergestützten Therapie. Rufen Sie uns an oder schreiben Sie uns eine E-Mail.
            </p>
            <div className="contact-details">
              <div className="contact-item">
                <div className="icon">📞</div>
                <div className="value">
                  <a href="tel:01621353794">0162 1353794</a>
                </div>
              </div>
              <div className="contact-item">
                <div className="icon">✉️</div>
                <div className="value">
                  <a href="mailto:derponystall@gmx.de">derponystall@gmx.de</a>
                </div>
              </div>
            </div>
          </ContactCard>
        </Container>
      </ContactSection>

      <ContactSection>
        <Container>
          <div className="text-center" style={{ marginTop: '2rem' }}>
            <h2 className="section-title">Therapie-Angebote buchen</h2>
            <p className="section-subtitle" style={{ marginBottom: '2rem' }}>
              Alle aktuellen Therapie-Angebote und Buchungsmöglichkeiten finden Sie auf unserer Kursübersicht.
            </p>
            <Link 
              to="/kurse" 
              style={{
                display: 'inline-block',
                padding: '1rem 2rem',
                background: 'linear-gradient(45deg, #2ecc71, #27ae60)',
                color: '#fff',
                textDecoration: 'none',
                borderRadius: 25,
                fontWeight: 600,
                transition: 'all 0.3s ease'
              }}
            >
              Zu den Buchungsoptionen
            </Link>
          </div>
        </Container>
      </ContactSection>
    </TherapieContainer>
  );
};

export default HeilpaedagogischesReiten;